import {
  BoxProps,
  InputProps,
  useAccordionItemContext,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CustomFieldEntityEnum,
  CustomFieldRecordSchema,
  CustomFieldTypeEnum,
  ICustomFieldMode,
  ICustomFieldRecordSchema,
  ICustomFieldResSchema,
} from '@monetize/types/app';
import { Fragment, FunctionComponent, ReactNode, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import {
  CUSTOM_FIELD_DATA_FORM_SPACING,
  CUSTOM_FORM_CONTENT_DIVIDER_VIEW_MODES,
} from '../../constants/customFields';
import { useCustomFields } from '../../hooks/useCustomFields';
import {
  convertFormValuesToReq,
  getCustomFieldGridTemplateColumns,
  sortCustomFields,
} from '../../utils/customFields';
import {
  MAccordion,
  MAccordionCustomButton,
  MAccordionItem,
  MAccordionPanel,
  MBox,
  MButton,
  MDivider,
  MFlex,
  MGrid,
  MGridItem,
  MSpinner,
  MText,
} from '../Monetize';
import { CustomFormDataFormField } from './CustomFormDataFormField';

// v3 dropped v2's render-prop children (`{({isExpanded}) => ...}`) on
// AccordionItem - the isExpanded-dependent content moves into its own child
// component reading `useAccordionItemContext()`, same pattern as Track H's
// other shared wrappers.
const CustomFieldAccordionBody = ({ children }: { children: ReactNode }) => {
  const { expanded: isExpanded } = useAccordionItemContext();

  return (
    <>
      <MAccordionCustomButton
        isExpanded={isExpanded}
        label="Additional Fields"
      ></MAccordionCustomButton>

      <MAccordionPanel>{children}</MAccordionPanel>
    </>
  );
};

/**
 * Page mode lays its fields out 4 across and has always ignored `fieldColSpan`,
 * with a gap of 4 rather than the one in CUSTOM_FIELD_DATA_FORM_SPACING (which
 * is 0 for this mode). Both pinned here so the responsive template reproduces
 * the existing full-width layout exactly.
 */
const PAGE_MODE_FIELD_COL_SPAN = 3;
const PAGE_MODE_GAP_REM = 1;

// Ensure stable data to avoid re-renders
const DEFAULT_EMPTY_VALUES: [
  ICustomFieldResSchema[],
  ICustomFieldResSchema[],
  ICustomFieldResSchema[],
] = [[], [], []];

export interface CustomFieldDataFormProps extends BoxProps {
  value: ICustomFieldRecordSchema;
  setValue: (val: ICustomFieldRecordSchema, shouldPersist?: boolean) => void;
  entity: CustomFieldEntityEnum;
  fieldColSpan?: number;
  mode?: ICustomFieldMode;
  accordionAutoExpanded?: boolean;
  accordionBorderColor?: string;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  variant?: InputProps['variant'];
  showDivider?: boolean;
  isHorizontal?: boolean;
  /**
   * Wraps each rendered field. Used to hang a validation anchor around the
   * control, which needs to enclose it rather than sit beside it.
   */
  renderFieldWrapper?: (
    customField: ICustomFieldResSchema,
    field: ReactNode,
  ) => ReactNode;
  saveOptions?: {
    handleOnSave: () => void;
    isLoading: boolean;
  };
}

const CustomFieldDataForm: FunctionComponent<CustomFieldDataFormProps> = ({
  value,
  setValue,
  entity,
  fieldColSpan = 3,
  mode = 'accordion',
  accordionAutoExpanded = false,
  accordionBorderColor,
  isDisabled,
  isReadOnly,
  variant = 'outline',
  showDivider = false,
  isHorizontal,
  renderFieldWrapper,
  saveOptions,
  ...rest
}: CustomFieldDataFormProps) => {
  const { customFieldList, zodSchemasByEntity, isLoading } =
    useCustomFields(entity);

  const applyFieldWrapper = (
    customField: ICustomFieldResSchema,
    field: ReactNode,
  ) => (renderFieldWrapper ? renderFieldWrapper(customField, field) : field);

  const zodSchema = zodSchemasByEntity[entity];

  const {
    control,
    getValues,
    formState: { isValid },
  } = useForm<ICustomFieldRecordSchema>({
    resolver: zodResolver(
      zodSchemasByEntity[entity] || CustomFieldRecordSchema,
    ) as any,
    mode: 'onChange',
    defaultValues: value,
    values: value,
    resetOptions: {
      keepDirtyValues: true,
      keepErrors: true,
    },
  });

  const [nonCheckBoxCustomFields, checkBoxCustomFields, orderedCustomFields] =
    useMemo(() => {
      if (!customFieldList) {
        return DEFAULT_EMPTY_VALUES;
      }
      const nonCheckBox = customFieldList
        .filter((field) => field.type !== CustomFieldTypeEnum.CHECKBOX)
        .sort(sortCustomFields);
      const checkBox = customFieldList
        .filter((field) => field.type === CustomFieldTypeEnum.CHECKBOX)
        .sort(sortCustomFields);
      return [nonCheckBox, checkBox, [...nonCheckBox, ...checkBox]] as const;
    }, [customFieldList]);

  const onSave = (field: string, value: any) => {
    const values = { ...getValues(), [field]: value };
    const parsedValue: Record<CustomFieldEntityEnum, any> =
      zodSchema.parse(values) || values;
    setValue(convertFormValuesToReq(parsedValue));
  };

  const spacingInfo = CUSTOM_FIELD_DATA_FORM_SPACING[mode];

  // Chakra's spacing scale is 4px-based; both the gap and the column maths are
  // derived from the one number so they can't drift apart
  const gapRem = spacingInfo.gridGap * 0.25;
  const fieldGridProps = {
    templateColumns: getCustomFieldGridTemplateColumns(fieldColSpan, gapRem),
    gap: `${gapRem}rem`,
  };

  if (isLoading) {
    return (
      <MBox w="100%" display="flex" alignItems="center" justifyContent="center">
        <MSpinner size="sm" />
      </MBox>
    );
  }

  if (customFieldList?.length === 0) {
    return null;
  }

  const formContent = (
    <>
      {mode === 'modal' && (
        <>
          <MDivider my="4" />
          <MText fontSize={16} fontWeight={600} mb={4}>
            Additional Fields
          </MText>
        </>
      )}
      {CUSTOM_FORM_CONTENT_DIVIDER_VIEW_MODES.has(mode) &&
        checkBoxCustomFields.length > 0 && <MDivider my="4" />}
      {nonCheckBoxCustomFields.length > 0 && (
        <MGrid
          {...fieldGridProps}
          mt={spacingInfo.gridTop}
          mb={spacingInfo.gridBottom}
        >
          {nonCheckBoxCustomFields?.map(
            (customField: ICustomFieldResSchema, index) => {
              return (
                // minW 0 so a field can shrink to its track — a grid item's
                // automatic minimum is its content, which a long field key
                // would otherwise push past the column and over the next one
                <MGridItem minW={0} key={index}>
                  {applyFieldWrapper(
                    customField,
                    <CustomFormDataFormField
                      customField={customField}
                      control={control}
                      onSave={onSave}
                      isDisabled={isDisabled}
                      isReadOnly={isReadOnly}
                      variant={variant}
                      isHorizontal={isHorizontal}
                    />,
                  )}
                </MGridItem>
              );
            },
          )}
        </MGrid>
      )}
      {mode === 'accordion' && <MBox my="6" />}

      <MGrid {...fieldGridProps} mb={spacingInfo.gridBottom}>
        {checkBoxCustomFields?.map(
          (customField: ICustomFieldResSchema, index) => {
            return (
              <MGridItem minW={0} key={index}>
                {applyFieldWrapper(
                  customField,
                  <CustomFormDataFormField
                    customField={customField}
                    control={control}
                    onSave={onSave}
                    isDisabled={isDisabled || isReadOnly}
                    isReadOnly={isReadOnly}
                    variant={variant}
                    isHorizontal={isHorizontal}
                  />,
                )}
              </MGridItem>
            );
          },
        )}
      </MGrid>

      {!!saveOptions && (
        <MFlex justifyContent="flex-end">
          <MButton
            isLoading={saveOptions.isLoading}
            onClick={saveOptions.handleOnSave}
            isDisabled={!isValid}
            type="submit"
            minW="auto"
          >
            Save
          </MButton>
        </MFlex>
      )}
    </>
  );

  return (
    <MBox w="100%" {...rest}>
      {showDivider && (
        <MDivider mt={spacingInfo.dividerTop} mb={spacingInfo.dividerBottom} />
      )}
      {mode === 'accordion' && (
        <MAccordion
          collapsible
          defaultValue={accordionAutoExpanded ? ['0'] : undefined}
        >
          <MAccordionItem value="0" borderColor={accordionBorderColor}>
            <CustomFieldAccordionBody>{formContent}</CustomFieldAccordionBody>
          </MAccordionItem>
        </MAccordion>
      )}
      {(mode === 'drawer' || mode === 'modal') && formContent}
      {mode === 'page' && (
        <MGrid
          // Page mode has always laid out 4 across and ignored fieldColSpan;
          // that ceiling is kept, and only the narrow end changes — a floor per
          // track, so a page column too narrow for 4 fields drops to 3, 2 or 1
          // instead of shrinking every field into a sliver.
          templateColumns={getCustomFieldGridTemplateColumns(
            PAGE_MODE_FIELD_COL_SPAN,
            PAGE_MODE_GAP_REM,
          )}
          gap={4}
          mb={6}
          alignItems="flex-end"
        >
          {orderedCustomFields.map((customField: ICustomFieldResSchema) => {
            return (
              <Fragment key={`${customField.entity}-${customField.key}`}>
                {applyFieldWrapper(
                  customField,
                  <CustomFormDataFormField
                    customField={customField}
                    control={control}
                    onSave={onSave}
                    isDisabled={isDisabled}
                    isReadOnly={isReadOnly}
                    variant={variant}
                    isHorizontal={isHorizontal}
                  />,
                )}
              </Fragment>
            );
          })}
        </MGrid>
      )}
    </MBox>
  );
};

export { CustomFieldDataForm };
